import dayjs from 'dayjs';
import _ from 'lodash';

import { detectType } from '@common/utils';

import { createBoundedLruCache } from './table.util';

import type { TSorts, TSortItem } from '@client/hooks/useTable';
import type { IDataType, EAntSort } from '@client/types/antd.type';

type TSortResolver = (value: unknown) => unknown;

// S1 fix: bounded LRU replaces unbounded Maps
const sorterFunctionCache = createBoundedLruCache<
  string,
  (a: unknown, b: unknown) => number
>(100);
const sorterObjectCache = createBoundedLruCache<string, TSorter<any>>(100);

const dateSort = (dateA: unknown, dateB: unknown): number =>
  dayjs(dateA as string).diff(dayjs(dateB as string));

const defaultSort = (a: number | string, b: number | string): number => {
  if (a < b) return -1;
  if (b < a) return 1;
  return 0;
};

// S2 fix: break only on non-null/undefined, not on any falsy (0, false, "")
const findInPath = (paths: string[] = [], data: unknown): unknown => {
  let result: unknown;
  for (const path of paths) {
    result = _.get(data, path);
    if (result !== undefined && result !== null) break;
  }
  return result;
};

const nestedSort =
  <T = IDataType>(paths: string[], resolver?: TSortResolver) =>
  (a: T, b: T): number => {
    const rawA = findInPath(paths, a);
    const rawB = findInPath(paths, b);

    const valA = resolver ? resolver(rawA) : rawA;
    const valB = resolver ? resolver(rawB) : rawB;

    // S4 fix: detect type from whichever side is non-null
    const type = detectType(valA ?? valB);

    if (type === 'date') return dateSort(valA, valB);
    return defaultSort(valA as number | string, valB as number | string);
  };

export const Sorter = {
  DEFAULT: defaultSort,
  NESTED: nestedSort,
  DATE: dateSort,
};

export type TSorter<T = IDataType> = {
  sorter:
    | ((a: T, b: T) => number)
    | { compare: (a: T, b: T) => number; multiple?: number };
  sortOrder: EAntSort | null;
};

/**
 * Creates a sorter configuration for a table column.
 * @param {TSorts} sortedInfo - Contains the current sorting information.
 * @param {string} sorterKey - The key used for sorting the column.
 * @param {string} [orderKey] - Key used to determine the sort order; defaults to sorterKey.
 * @param {number} multiple - Optional priority for multi-column sorting.
 * @param {TSortResolver} resolver - Optional function to resolve the value for sorting.
 * @returns { sorter; sortOrder } Column sorter config for Ant Design Table.
 */
export const columnSorter = <T extends IDataType>(
  sortedInfo: TSorts,
  sorterKey: string,
  orderKey?: string,
  multiple?: number,
  resolver?: TSortResolver,
): TSorter<T> => {
  const resolvedOrderKey = orderKey ?? sorterKey;

  let sortOrder: EAntSort | null = null;

  if (Array.isArray(sortedInfo)) {
    const match = sortedInfo.find(
      (s: TSortItem) => s && s.columnKey === resolvedOrderKey && s.order,
    );
    sortOrder = (match?.order ?? null) as EAntSort | null;
  } else if (sortedInfo && typeof sortedInfo === 'object') {
    const s = sortedInfo as TSortItem;
    sortOrder = (
      s.columnKey === resolvedOrderKey ? (s.order ?? null) : null
    ) as EAntSort | null;
  }

  const resolverFlag = resolver ? 'withResolver' : 'noResolver';
  const functionCacheKey = `${resolvedOrderKey}:${resolverFlag}`;

  let compare = sorterFunctionCache.get(functionCacheKey) as
    ((a: T, b: T) => number) | undefined;
  if (!compare) {
    compare = Sorter.NESTED<T>([resolvedOrderKey], resolver);
    sorterFunctionCache.set(
      functionCacheKey,
      compare as (a: unknown, b: unknown) => number,
    );
  }

  const objectCacheKey = `${functionCacheKey}:${multiple ?? 'single'}:${sortOrder ?? 'null'}`;

  let cachedSorter = sorterObjectCache.get(objectCacheKey) as
    TSorter<T> | undefined;
  if (!cachedSorter) {
    cachedSorter = {
      sorter:
        typeof multiple === 'number'
          ? { compare, multiple }
          : (compare as (a: T, b: T) => number),
      sortOrder,
    } as TSorter<T>;
    sorterObjectCache.set(objectCacheKey, cachedSorter);
  }

  return cachedSorter;
};
